import {
    CaseStatusTypesEnum,
    CaseSubStatusTypesEnum,
    FavoriteTypesEnum,
    SortColumns,
} from '../../utilities/Constants';
import { ICaseModel, ICaseSearchModel } from '../../interfaces/ICase';
import { cloneDeep, isEmpty } from 'lodash';
import { use, useEffect, useState } from 'react';
import { useGetCaseList, useGetCaseListKey } from '../../components/case-list/useGetCaseList';

import { CaseListPage } from './CaseListPage';
import { DocumentTitle } from '../../components/shared/DocumentTitle';
import { IOrganizationModel } from '../../interfaces/IOrganization';
import { IPagedQueryModel } from '../../interfaces/IQuery';
import { Loader } from '../../components/shared/Loader';
import { SortDirections } from '../../interfaces/PagedSortedSearch';
import UIHelper from '../../utilities/UIHelper';
import { showErrorToast } from '../../shared/ToastUtils';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../contexts/AuthContext';
import { useCaseManagersInZone } from '../../shared/react-query-hooks/useCaseManagersInZone';
import { useCaseStatusTypesLookup } from '../../shared/react-query-hooks/useCaseStatusTypesLookup';
import { useDidUpdate } from '@mantine/hooks';
import { useGetCaseListCount } from '../../components/case-list/useGetCaseListCount';
import { useGetDefaultUserOrg } from '../../shared/react-query-hooks/useGetDefaultUserOrg';
import { useGetDiagnoses } from '../../shared/react-query-hooks/useGetDiagnoses';
import { useGetFavorites } from '../../shared/react-query-hooks/useGetFavorites';
import { useGetPlaintiffsFirms } from '../../shared/react-query-hooks/useGetPlaintiffsFirms';
import { useGetQScores } from '../../shared/react-query-hooks/useGetQScores';
import { useGetUsStatesLookup } from '../../shared/react-query-hooks/useUsStatesLookup';
import { useGetZoneJurisdictions } from '../../shared/react-query-hooks/useGetZoneJurisdictions';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useZoneContext } from '../../contexts/useZoneContext';

const DefaultPageSize = 100;

export const DefaultCaseSearchModel: ICaseSearchModel = {
    statusIds: [CaseStatusTypesEnum.Open.Value],
    subStatusIds: [CaseSubStatusTypesEnum.Active.Value],
    queryModel: {
        pageNumber: 0,
        pageSize: DefaultPageSize,
        sortBy: SortColumns.CaseName,
        sortDirection: SortDirections.Asc,
    },
};

function CaseListPageWrapper() {
    const { key } = useLocation();
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();
    const queryClient = useQueryClient();

    const [pendingSearchModel, setPendingSearchModel] =
        useState<ICaseSearchModel>(DefaultCaseSearchModel);
    const [appliedSearchModel, setAppliedSearchModel] =
        useState<ICaseSearchModel>(pendingSearchModel);
    const [isFavoriteApplied, setIsFavoriteApplied] = useState<boolean>(false);

    const caseStatusTypesApi = useCaseStatusTypesLookup();
    const usStatesApi = useGetUsStatesLookup();
    const diagnonsesApi = useGetDiagnoses();
    const caseManagersApi = useCaseManagersInZone();
    const defaultFavoriteApi = useGetFavorites(FavoriteTypesEnum.CaseList, true);
    const jurisdictionsApi = useGetZoneJurisdictions();
    const qScoresApi = useGetQScores();
    const plaintiffsFirmsApi = useGetPlaintiffsFirms();
    const defaultUserOrgApi = useGetDefaultUserOrg();

    const casesApi = useGetCaseList(appliedSearchModel, isFavoriteApplied);
    const casesCountApi = useGetCaseListCount(appliedSearchModel, isFavoriteApplied);

    const showLoader =
        caseStatusTypesApi.isLoading ||
        usStatesApi.isLoading ||
        diagnonsesApi.isLoading ||
        caseManagersApi.isLoading ||
        defaultFavoriteApi.isLoading ||
        jurisdictionsApi.isLoading ||
        qScoresApi.isLoading ||
        plaintiffsFirmsApi.isLoading ||
        defaultUserOrgApi.isLoading;

    const handleSearchModelChange = (updatedSearchModel: ICaseSearchModel) => {
        setPendingSearchModel(updatedSearchModel);
    };

    const handleSearch = (updatedSearchModel: ICaseSearchModel, fromCurrentPage = false) => {
        const { queryModel = {} as IPagedQueryModel } = updatedSearchModel;

        const pageSpecificSearchModel: ICaseSearchModel = {
            ...updatedSearchModel,
            queryModel: {
                ...queryModel,
                pageNumber: fromCurrentPage ? queryModel.pageNumber : 0,
            },
        };

        setPendingSearchModel(pageSpecificSearchModel);
        setAppliedSearchModel(pageSpecificSearchModel);

        const differences = UIHelper.difference(appliedSearchModel, pageSpecificSearchModel);
        const shouldForceRefresh = isEmpty(differences);

        toast.dismiss();
        if (shouldForceRefresh) {
            casesApi.refetch();
        }
    };

    const handleResetFilters = () => {
        setPendingSearchModel(DefaultCaseSearchModel);
        setAppliedSearchModel(DefaultCaseSearchModel);
    };

    const handleUpdateCase = (updatedCase: ICaseModel) => {
        const casesCopy = cloneDeep(casesApi.data) ?? [];
        const foundIdx = casesCopy.findIndex((c) => c.guid === updatedCase.guid);

        if (foundIdx > -1) {
            casesCopy.splice(foundIdx, 1, updatedCase);
        }

        queryClient.setQueryData(
            [useGetCaseListKey, { searchModel: appliedSearchModel }],
            (prevData: ICaseModel[] | undefined) => {
                if (!prevData) return [];
                return prevData.map((c) => (c.guid === updatedCase.guid ? updatedCase : c));
            }
        );
    };

    const handleDefaultFavorite = async () => {
        setIsFavoriteApplied(true);

        const defaultFavorite = defaultFavoriteApi.data?.find((favorite) => favorite.default);
        if (defaultFavorite?.settingJson) {
            const favoriteSearchModel: ICaseSearchModel = JSON.parse(defaultFavorite.settingJson);
            handleSearch(favoriteSearchModel);
        }
    };

    useDidUpdate(() => {
        if (defaultFavoriteApi.isSuccess) {
            handleDefaultFavorite();
        }
    }, [defaultFavoriteApi.isSuccess]);

    useEffect(() => {
        if (!defaultFavoriteApi.isSuccess) return;

        setIsFavoriteApplied(true);
        handleDefaultFavorite();
    }, [defaultFavoriteApi.isSuccess, key]);

    useEffect(() => {
        if (casesApi.isError) {
            const errorMessage = (casesApi.error as Error)?.message;
            showErrorToast(errorMessage ?? 'Failed to retrieve cases');
        }
    }, [casesApi.isError]);

    if (showLoader) {
        return <Loader />;
    }

    return (
        <>
            <DocumentTitle title="Case List" />
            <CaseListPage
                appliedSearchModel={appliedSearchModel}
                cases={casesApi.data ?? []}
                caseCount={casesCountApi.data ?? 0}
                defaultUserOrg={defaultUserOrgApi.data ?? ({} as IOrganizationModel)}
                isFetching={casesApi.isFetching}
                lookups={{
                    caseManagers: caseManagersApi.data ?? [],
                    caseStatuses: caseStatusTypesApi.data ?? [],
                    diagnoses: diagnonsesApi.data ?? [],
                    favorites: defaultFavoriteApi.data ?? [],
                    jurisdictions: jurisdictionsApi.data ?? [],
                    plaintiffsFirms: plaintiffsFirmsApi.data ?? [],
                    qScores: qScoresApi.data ?? [],
                    usStates: usStatesApi.data ?? [],
                }}
                key={key}
                onApplyFilters={handleSearchModelChange}
                onResetFilters={handleResetFilters}
                onSearch={handleSearch}
                onUpdateCase={handleUpdateCase}
                pendingSearchModel={pendingSearchModel}
                refreshIdentifier={key}
                reloadFavorites={defaultFavoriteApi.refetch}
                user={user}
                zoneContext={zoneContext}
            />
        </>
    );
}

export default CaseListPageWrapper;
